import React, { useState, useEffect } from 'react'

interface OfflinePageProps {
  onRetry: () => Promise<boolean>
  isChecking: boolean
}

/**
 * Beautiful offline state page displayed when internet connection is lost.
 * Features an elegant glass design with animated elements matching the app's aesthetic.
 */
export function OfflinePage({ onRetry, isChecking }: OfflinePageProps) {
  const [pulseKey, setPulseKey] = useState(0)
  const [retryCount, setRetryCount] = useState(0)

  // Trigger pulse animation on retry
  const handleRetry = async () => {
    setPulseKey(prev => prev + 1)
    setRetryCount(prev => prev + 1)
    await onRetry()
  }

  // Auto-retry when connection is restored
  useEffect(() => {
    const handleOnline = () => {
      onRetry()
    }
    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [onRetry])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-6 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gray-700/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gray-600/15 rounded-full blur-3xl animate-pulse delay-1000" />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(to right, white 1px, transparent 1px),
                              linear-gradient(to bottom, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
        
        {/* Radial gradient center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-gray-700/10 to-transparent rounded-full" />
      </div>

      {/* Main content card */}
      <div className="relative z-10 max-w-md w-full">
        {/* Glass card */}
        <div 
          className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl"
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
          }}
        >
          {/* Subtle inner glow */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
          
          {/* Icon container with animation */}
          <div className="relative flex justify-center mb-8">
            <div 
              key={pulseKey}
              className="relative"
            >
              {/* Outer ring animation */}
              <div className="absolute inset-0 animate-ping">
                <div className="w-24 h-24 rounded-full border-2 border-white/20" />
              </div>
              
              {/* Main icon circle */}
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border border-white/10 flex items-center justify-center shadow-xl">
                {/* Disconnected signal icon */}
                <svg 
                  className="w-12 h-12 text-white/80"
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  {/* WiFi waves */}
                  <path d="M1 9a10 10 0 0 1 14-1" className="opacity-30" />
                  <path d="M5 13a6 6 0 0 1 8.5 0" className="opacity-50" />
                  <path d="M8.5 16.5a3 3 0 0 1 4 0" className="opacity-70" />
                  {/* Center dot */}
                  <circle cx="12" cy="20" r="1" fill="currentColor" />
                  {/* Slash through */}
                  <line x1="2" y1="2" x2="22" y2="22" className="text-red-400/80" strokeWidth="2" />
                </svg>
              </div>
              
              {/* Subtle glow */}
              <div className="absolute -inset-4 bg-white/5 rounded-full blur-xl pointer-events-none" />
            </div>
          </div>

          {/* Text content */}
          <div className="relative text-center mb-8">
            <h1 className="text-2xl font-semibold text-white mb-3 tracking-tight">
              You're Offline
            </h1>
            <p className="text-gray-400 text-base leading-relaxed">
              It looks like your internet connection was lost. 
              Check your network settings and try again.
            </p>
          </div>

          {/* Retry button */}
          <div className="relative">
            <button
              onClick={handleRetry}
              disabled={isChecking}
              className="w-full py-4 px-6 bg-white text-gray-900 font-medium rounded-xl 
                         transition-all duration-300 ease-out
                         hover:bg-gray-100 hover:shadow-lg hover:shadow-white/10
                         active:scale-[0.98] active:bg-gray-200
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white
                         flex items-center justify-center gap-3"
            >
              {isChecking ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-gray-900 rounded-full animate-spin" />
                  <span>Checking connection...</span>
                </>
              ) : (
                <>
                  <svg 
                    className="w-5 h-5" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-9-9" />
                    <polyline points="21 3 21 9 15 9" />
                  </svg>
                  <span>Try Again</span>
                </>
              )}
            </button>
          </div>

          {/* Retry count indicator */}
          {retryCount > 0 && (
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Retry attempt {retryCount}
              </p>
            </div>
          )}

          {/* Help text */}
          <div className="mt-8 pt-6 border-t border-white/5">
            <div className="flex items-start gap-3 text-sm text-gray-500">
              <svg 
                className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-600" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              <div>
                <p className="text-gray-400 mb-1">Troubleshooting tips:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-500">
                  <li>Check your WiFi or mobile data</li>
                  <li>Try toggling airplane mode</li>
                  <li>Move closer to your router</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom brand indicator */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gray-600 animate-pulse" />
            Insight Atlas
          </p>
        </div>
      </div>
    </div>
  )
}

export default OfflinePage
